/** Client-side handling of PSE companion snapshot files. Nothing is uploaded until staff choose to. */
export const SNAPSHOT_EXTENSION = ".psejson"
/** Browsers cannot infer a type for an unknown extension, so the dialog filters by name. */
export const SNAPSHOT_ACCEPT = `${SNAPSHOT_EXTENSION},application/json`
export const MAX_SNAPSHOT_BYTES = 2 * 1024 * 1024
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface SnapshotUpload { uploadId: string; snapshotJson: string; sha256: string }
export interface SnapshotIdentity { applicationId: string; programYear: number; agencyId: number; objects: number }

export interface SnapshotReceipt {
  snapshotId: string; applicationId: string; programYear: number; agencyId: number
  profile: string; sha256: string; sizeBytes: number; storedAt: string; status: string
}

export interface SnapshotCandidateApplication {
  benefitId: string; fileNumber: number; programYear: number; address: string
  pseApplicationType?: number | null
}

export interface SnapshotMatchCandidate {
  personId: string; firstName: string; lastName: string; dateOfBirth: string | null
  ssnLast4: string | null; confidenceScore: number; matchType: string; matchedFields: string[]
  previousApplications?: SnapshotCandidateApplication[] | null
}

export interface SnapshotHouseholdMember {
  name: string | null; firstName: string | null; lastName: string | null
  dateOfBirth: string | null; ssnLast4: string | null
  isApplicant: boolean; isDisabled: boolean; candidates: SnapshotMatchCandidate[]
  sourceOccupantId?: string | null
}

export interface SnapshotSummary {
  applicationId: string; programYear: number; agencyId: number
  agencyName: string | null; status: string | null; statusReason: string | null
  applicantFirstName: string | null; applicantLastName: string | null; applicantDateOfBirth: string | null
  city: string | null; state: string | null; postalCode: string | null
  householdMemberCount: number; incomeRecordCount: number
  reportedMonthlyIncome: number | null; electricPledge: number | null; gasPledge: number | null
  capturedAt: string; sourceObjectCount: number
  isComplete: boolean; missingSections: string[] | null
}

/** PSE's section names, in the words staff see in the portal. */
const STEP_NAMES: Record<string, string> = {
  householdStep: "household", profileStep: "profile", appointmentStep: "appointment",
  step4: "payment option", housingStep: "housing", pledgeStep: "pledge",
  statusStep: "status", calculations: "calculations",
}

/** Describes an application PSE has not finished, in terms a staff member can act on. */
export function incompleteNotice(summary: SnapshotSummary): string | null {
  if (summary.isComplete) return null
  const steps = (summary.missingSections ?? []).map(name => STEP_NAMES[name] ?? name)
  if (steps.length === 0) return "This application is still in progress in PSE."
  return `Still in progress in PSE: no ${steps.join(", ")} step yet. Everything PSE does hold was imported.`
}

export interface SnapshotReview {
  receipt: SnapshotReceipt; summary: SnapshotSummary
  household: SnapshotHouseholdMember[]; matchState: string; matchUnavailableReason: string | null
  authorizedIdentifiers?: Record<string, string> | null
}

export interface SnapshotMemberDecision {
  memberIndex: number
  decision: "no" | "use_existing" | "create_new"
  selectedPersonId?: string | null
}

export interface CompanionStatusDto {
  connected: boolean
  signedInToPse: boolean
  idle: boolean
  allowDispatch: boolean
  ready: boolean
  updatedAt?: string | null
}

export interface CompanionDispatchResult {
  requestId: string
  status: string
  snapshotId?: string | null
  message?: string | null
}

/** Presentation only; the server validates evidence and owns the final mapping. */
export function snapshotDestination(
  member: Pick<SnapshotHouseholdMember, "firstName" | "lastName">,
  source: { accountHolder: { firstName: string; lastName: string }; coCustomer?: { firstName: string; lastName: string } | null } | null,
): string {
  const normalize = (name: string | null | undefined) => (name ?? "").trim().replace(/\s+/g, " ").toUpperCase()
  const same = (person: { firstName: string; lastName: string } | null | undefined) => person &&
    normalize(member.firstName) !== "" && normalize(member.lastName) !== "" &&
    normalize(member.firstName) === normalize(person.firstName) && normalize(member.lastName) === normalize(person.lastName)
  const holder = same(source?.accountHolder)
  const co = same(source?.coCustomer)
  return holder && co ? "Needs identity review (multiple source roles)" : holder ? "Account holder" : co ? "Co-applicant (co-customer)" : "Additional household member (server checks identity)"
}

/** Reads the file, checks it is a companion snapshot, and computes the transfer checksum. */
export async function prepareSnapshot(snapshotJson: string, uploadId: string = crypto.randomUUID()): Promise<SnapshotUpload> {
  if (typeof snapshotJson !== "string" || snapshotJson.length > MAX_SNAPSHOT_BYTES || !uuid.test(uploadId)) throw new Error("Invalid or oversized snapshot transfer.")
  const bytes = new TextEncoder().encode(snapshotJson)
  if (bytes.length > MAX_SNAPSHOT_BYTES) throw new Error("Snapshot exceeds 2 MiB.")
  snapshotIdentity(snapshotJson)
  const digest = await crypto.subtle.digest("SHA-256", bytes)
  const sha256 = Array.from(new Uint8Array(digest), value => value.toString(16).padStart(2, "0")).join("")
  return { uploadId, snapshotJson, sha256 }
}

/** Identifies the file without displaying any source content. */
export function snapshotIdentity(snapshotJson: string): SnapshotIdentity {
  try {
    const value = JSON.parse(snapshotJson)
    const identity = value.identity
    if (value.formatVersion !== 1 || value.profile !== "persisted-application-v1" ||
      typeof identity?.applicationId !== "string" || !/^[0-9]{1,20}$/.test(identity.applicationId) ||
      !Number.isInteger(identity.programYear) || !Number.isInteger(identity.agencyId) ||
      !Array.isArray(value.objects) || value.objects.length > 5000) throw new Error()
    return { applicationId: identity.applicationId, programYear: identity.programYear, agencyId: identity.agencyId, objects: value.objects.length }
  } catch { throw new Error("This is not a PSE companion snapshot file. Nothing was uploaded.") }
}

export function receiptMatches(receipt: SnapshotReceipt, upload: SnapshotUpload): boolean {
  return receipt?.status === "stored" && receipt.snapshotId === upload.uploadId && receipt.sha256 === upload.sha256
}

/** A wrong extension is a warning, not a refusal: the contents decide. */
export function hasSnapshotExtension(fileName: string): boolean {
  return fileName.toLowerCase().endsWith(SNAPSHOT_EXTENSION)
}

export function fullName(member: { firstName: string | null; lastName: string | null; name: string | null }): string {
  const joined = [member.firstName, member.lastName].filter(Boolean).join(" ").trim()
  return joined || member.name || "Unnamed household member"
}
